import { ArrowUpIcon, AttachmentIcon, QuestionIcon } from "@chakra-ui/icons";
import {
  Box,
  VStack,
  Image,
  InputGroup,
  InputRightElement,
  InputLeftElement,
  IconButton,
  Text,
  UnorderedList,
  ListItem,
  Textarea,
  Avatar,
  Button,
  Flex
} from "@chakra-ui/react";
import { decryptResponse } from "../../allApi/interceptorsApiCall";
import React, { useEffect, useRef, useState } from "react";
import { WordByWordParagraph } from "../personalAssistance/Wordplay";
import { CloseIcon } from "@chakra-ui/icons";
import chatbot_agentPhoto from "../../assets/waveflowmain/chatbot_agentPhoto.png";
import apiClient from "../../allApi/interceptorsApiCall";
import { commonStyle } from "../../styles/commonStyle";
import SkeletonChat from "../../components/loader/skeletonLoader/SkeletonChat";
import pdfIcon from "../../assets/waveflowmain/filesIcon/pdf.png";
import docIcon from "../../assets/waveflowmain/filesIcon/doc.png";
import jsIcon from "../../assets/waveflowmain/filesIcon/js.png";
import pyIcon from "../../assets/waveflowmain/filesIcon/py.png";
import jsonIcon from "../../assets/waveflowmain/filesIcon/json.png";
import txtIcon from "../../assets/waveflowmain/filesIcon/txt.png";
import csvIcon from "../../assets/waveflowmain/filesIcon/csv.png";
import { useCustomTost } from "../../utiils/useCustomTost";
import MarkdownRenderer from "../../components/stringtomarkdown/Stringtomarkdown";

function ChatBotPage() {
  const [usermanage, setUserManage] = useState({});
  const [allChatData, setAllChatData] = useState([]);
  const [queChat, setQueChat] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitFormStatus, setSubmitFormStatus] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const toast = useCustomTost();
  const waveflowRef = useRef();
  const messagesEndRef = useRef(null);
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [allChatData]);

  // Get appropriate icon for file type
  const getFileIcon = (fileName) => {
    if (!fileName) return pdfIcon;
    const extension = fileName?.split(".")?.pop()?.toLowerCase();
    const icons = {
      pdf: pdfIcon,
      doc: docIcon,
      docx: docIcon,
      py: pyIcon,
      js: jsIcon,
      txt: txtIcon,
      csv: csvIcon,
      json: jsonIcon,
    };
    return icons[extension] || pdfIcon;
  };

  // Upload a single file to the server
  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await apiClient.post("/file", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      });

      const decryptedData = decryptResponse(response.data);

      if (decryptedData?.status === "uploaded") {
        setUploadedFiles((prev) => [...prev, decryptedData.filename]);
        // toast("Success", `${file.name} uploaded successfully`, "success");
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error uploading file", error);
      toast(
        "Error",
        decryptResponse(error?.response?.data?.error) || `Failed to upload ${file.name}`,
        "error"
      );
      return false;
    } finally {
      setUploadProgress(0);
    }
  };

  // Handle file selection from input
  const handleFileChange = async (e) => {
    const selectedFiles = Array.from(e.target.files);

    if (!selectedFiles || selectedFiles.length === 0) return;

    const remainingSlots = 10 - files.length;

    if (remainingSlots <= 0) {
      toast("Warning", "Maximum 10 files allowed", "warning");
      return;
    }

    // Only take up to the remaining slots
    const filesToProcess = selectedFiles.slice(0, remainingSlots);

    if (selectedFiles.length > remainingSlots) {
      toast(
        "Warning",
        `Only ${remainingSlots} more files can be added`,
        "warning"
      );
    }

    // Add files to UI first (optimistic update)
    setFiles((prev) => [...prev, ...filesToProcess]);

    // Then upload each file
    setIsLoading(true);

    try {
      for (const file of filesToProcess) {
        const success = await uploadFile(file);

        if (!success) {
          // Remove from UI if upload failed
          setFiles((prev) => prev.filter((f) => f.name !== file.name));
        }
      }
    } catch (error) {
      toast("Error", "Failed to process uploads", "error");
    } finally {
      setIsLoading(false);
      // Reset file input
      e.target.value = "";
    }
  };

  // Remove a file
  const handleDeleteFile = (index) => {
    const fileToRemove = files[index];

    // Remove from UI
    setFiles((prev) => prev.filter((_, i) => i !== index));

    // Remove from uploaded files list if present
    if (fileToRemove) {
      setUploadedFiles((prev) =>
        prev.filter((filename) => filename !== fileToRemove.name)
      );
    }
  };

  // Send a chat message
  const handleSubmit = () => {
    if (!queChat.trim()) return;

    // Add user message to chat
    setAllChatData((prev) => [
      ...prev,
      { que: queChat, ans: "", loading: true },
    ]);

    // Send to API
    sendChatQuery(queChat);

    // Reset input
    setQueChat("");
    waveflowRef.current.style.height = "45px";
    setSubmitFormStatus(true);
  };

  const processBackendResponse = (answer = "") => {
    if (!answer) return { isError: false, text: "" };

    const textToCheck = answer.toLowerCase();

    // 1. Check for Quota/Rate Limit (429)
    if (
      textToCheck.includes("'code': 429") ||
      textToCheck.includes('"code": 429') ||
      textToCheck.includes("error code: 429") ||
      textToCheck.includes("resource_exhausted")
    ) {
      return {
        isError: true,
        text: "⚠️ You exceeded your current quota. Please check your plan and billing details.",
      };
    }

    // 2. Check for Bad Request / Model Not Available (400)
    if (
      textToCheck.includes("'code': 400") ||
      textToCheck.includes('"code": 400') ||
      textToCheck.includes("error code: 400") ||
      textToCheck.includes("Error code: 400") 
    ) {
      return {
        isError: true,
        text: "⚠️ Invalid Request: The selected model is currently not available or the request was malformed.",
      };
    }

    // 3. Check for other General LLM Errors
    const genericErrorPatterns = [
      "unsupported parameter",
      "invalid_request_error",
      "max_tokens",
      "use 'max_completion_tokens'",
      "unexpected error",
      "error code:", // Catch-all for other codes like 500
    ];

    if (genericErrorPatterns.some((p) => textToCheck.includes(p))) {
      return {
        isError: true,
        text: "❗ This model does not support chat interactions. Please switch to a supported model.",
      };
    }

    // 4. No Error Detected
    return { isError: false, text: answer };
  };

  // Process the chat query
  const sendChatQuery = async (question) => {
    const currentIndex = allChatData.length;
    const formData = new FormData();

    formData.append("query", question);

    // Use the actual uploaded filenames instead of hardcoded values
    if (uploadedFiles && uploadedFiles.length > 0) {
      formData.append("filenames", JSON.stringify(uploadedFiles));
    }

    try {
      const response = await apiClient.post("/chat_pdf", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const rawAnswer = decryptResponse(response.data).final_answer || "No response received";
      
      // Use the generic parser to check for errors in the answer
      const result = processBackendResponse(rawAnswer);

      // Update the chat with the clean answer
      setAllChatData((prev) =>
        prev.map((item, idx) =>
          idx === currentIndex ? { ...item, ans: result.text, loading: false } : item
        )
      );
    } catch (error) {
      console.error("Chat API error:", error);

      // Show error in chat
      setAllChatData((prev) =>
        prev.map((item, idx) =>
          idx === currentIndex
            ? {
                ...item,
                ans: "Sorry, I couldn't process your request.",
                loading: false,
              }
            : item
        )
      );
    }
  };

  // Handle text area input and auto-resize
  const handleQueryWaveFlow = (e) => {
    setQueChat(e.target.value);

    const textarea = waveflowRef.current;
    if (textarea) {
      textarea.style.height = "45px";
      textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
    }
  };

  // Handle key presses in chat input
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };


  return (
    <VStack justifyContent="space-between" top={0} h="80vh" w="100%" m="auto">
      {/* Chat Messages Area */}
      <Box
        overflowY="auto"
        sx={{ ...commonStyle.sx, scrollBehavior: "smooth" }}
        h="90%"
        w="100%"
      >
        {allChatData.map((message, index) => (
          <Box
            key={index}
            display="flex"
            w="100%"
            flexDirection="column"
            mb={2}
          >
            {/* User Message */}
            {message.que && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                ml="10%"
              >
                <Box
                  fontSize="12px"
                  mr={2}
                  bg="gray.100"
                  boxShadow="inset 0 0 12px rgba(143, 141, 141, 0.3)"
                  color="#232627"
                  px={3}
                  py={3}
                  borderRadius="md"
                  borderBottomRightRadius="none"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {message.que}
                </Box>
                <Avatar
                  boxSize="25px"
                  name={usermanage?.name}
                  src={usermanage?.picture}
                  alt={usermanage?.name}
                  p={0}
                  objectFit="cover"
                />
              </Box>
            )}

            {/* Bot Response */}
            {message.ans ? (
              <Box mr="10%" w="100%" display="flex" alignItems="center" mt={2}>
                <Image
                  boxSizing="border-box"
                  w={6}
                  src={chatbot_agentPhoto}
                  alt="Bot Logo"
                />
                <Box
                  w="85%"
                  ml={2}
                  bg="#D1C5E3"
                  p={3}
                  borderRadius="md"
                  boxShadow="inset 0 0 12px rgba(133, 121, 134, 0.3)"
                  borderBottomLeftRadius="none"
                >
                  {/* <WordByWordParagraph
                    submitFormStatus={submitFormStatus}
                    paragraph={message.ans}
                  /> */}
                  <MarkdownRenderer content={message?.ans}/>
                </Box>
              </Box>
            ) : message.loading ? (
              <Box ml={8} mt={2}>
                <SkeletonChat />
              </Box>
            ) : null}
          </Box>
        ))}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      <Box w="100%" boxSizing="border-box" bg="#fff">
        {/* Uploaded Files List */}
        <Box alignItems="flex-start" mt={2} w="100%">
          {files?.length > 0 && <Text fontWeight="bold">Uploaded Files:</Text>}
          {files?.length > 0 && (
            <UnorderedList
              h="100px"
              styleType="disc"
              ml={4}
              overflowY="auto"
              sx={{ ...commonStyle.sx, scrollBehavior: "smooth" }}
            >
              {files?.map((file, idx) => (
                <ListItem
                  textAlign="left"
                  key={idx}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box display="flex" alignItems="center">
                    <Image
                      src={getFileIcon(file?.name)}
                      alt="File Icon"
                      boxSize="20px"
                      mr={2}
                    />
                    {file.name}
                    {uploadedFiles.includes(file.name) && (
                      <Text fontSize="xs" ml={2} color="green.500">
                        (Uploaded)
                      </Text>
                    )}
                  </Box>
                  <IconButton
                    icon={<CloseIcon />}
                    size="sm"
                    aria-label="Delete file"
                    onClick={() => handleDeleteFile(idx)}
                  />
                </ListItem>
              ))}
            </UnorderedList>
          )}
        </Box>

        {/* Upload Progress Bar */}
        {uploadProgress > 0 && (
          <Box mb={2}>
            <Text fontSize="sm">Uploading: {uploadProgress}%</Text>
            <Box bg="gray.200" h="6px" borderRadius="md" overflow="hidden">
              <Box
                bg="#6866D3"
                h="100%"
                width={`${uploadProgress}%`}
                transition="width 0.3s"
              />
            </Box>
          </Box>
        )}

        <Flex
          border="1px solid lightgray"
          borderRadius="10px"
          bg="white"
          px={2}
          py={1}
          alignItems="flex-end"
          w="100%"
        >
          {/* Attachment Icon */}
          <Box>
            <input
              type="file"
              id="files"
              accept=".pdf, .doc, .docx, .txt, .csv, .json, .ipynb, .py"
              multiple
              onChange={(e) => handleFileChange(e, files, setFiles, toast)}
              style={{ display: "none" }}
            />
            <IconButton
              icon={<AttachmentIcon />}
              size="sm"
              aria-label="Attach"
              onClick={() => document.getElementById("files").click()}
              isDisabled={isLoading}
              variant="ghost"
              mb={1}
            />
          </Box>

          {/* Textarea */}
          <Textarea
            ref={waveflowRef}
            value={queChat}
            onChange={handleQueryWaveFlow}
            placeholder="What would you like to know?"
            focusBorderColor="transparent"
            _focus={{ boxShadow: 'none' }}
            border="none"
            resize="none"
            minH="40px"
            maxH="150px"
            overflowY="auto"
            lineHeight="1.5"
            onKeyDown={handleKeyDown}
            mx={2}
            flex={1}
          />

          {/* Send Button */}
          <Box>
            <IconButton
              icon={<ArrowUpIcon />}
              aria-label="Send"
              size="sm"
              bg="#6866D3"
              color="white"
              borderRadius="full"
              _hover={{ bg: "#5755c9" }}
              onClick={handleSubmit}
              mb={1}

            />
          </Box>
        </Flex> 
      </Box>
    </VStack>
  );
}

export default ChatBotPage;
